if (!customElements.get('quick-view-modal')) {
  customElements.define(
    'quick-view-modal',
    class QuickViewModal extends DrawerComponent {
      constructor() {
        super();

        this.classesToRemoveOnLoad = 'drawer--loading';
        this.drawerBody = this.querySelector(this.selector);
      }

      get selector() {
        return '.quick-view__content';
      }

      get requiresBodyAppended() {
        return !(Shopify.designMode && this.closest('.section-group-overlay-quick-view'));
      }

      prepareToShow() {
        super.prepareToShow();
        if (this.querySelector('product-info')) return;
        this.quickview();
      }

      handleAfterShow() {
        super.handleAfterShow();
        document.dispatchEvent(
          new CustomEvent('quick-view:open', {
            detail: { productUrl: this.dataset.productUrl },
          })
        );
      }

      handleAfterHide() {
        super.handleAfterHide();
        const drawerContent = this.querySelector(this.selector);
        drawerContent.innerHTML = '';
        this.classList.add(this.classesToRemoveOnLoad);
      }

      getProductQuickViewSectionId() {
        let sectionId = FoxTheme.QuickViewSectionId || false;

        if (!sectionId) {
          const productQuickView = document.querySelector('.section-group-overlay-quick-view');
          if (productQuickView) {
            sectionId = FoxTheme.utils.getSectionId(productQuickView);
          }
        }

        if (!sectionId) {
          sectionId = 'quick-view';
        }

        FoxTheme.QuickViewSectionId = sectionId;
        return sectionId;
      }

      /**
       * @param {Document} doc
       * @returns {Element|null}
       */
      findQuickViewContentRoot(doc) {
        return (
          doc.querySelector(this.selector) ||
          doc.querySelector('#MainProduct-quick-view__content') ||
          doc.querySelector('product-info')?.closest('.quick-view__content') ||
          doc.querySelector('product-info')?.parentElement
        );
      }

      /**
       * @param {string} responseText
       * @returns {string}
       */
      extractQuickViewInnerHtml(responseText) {
        const doc = new DOMParser().parseFromString(responseText, 'text/html');
        const contentRoot = this.findQuickViewContentRoot(doc);

        if (contentRoot) {
          return contentRoot.innerHTML;
        }

        const productInfo = doc.querySelector('product-info');
        if (productInfo) {
          return productInfo.outerHTML;
        }

        return '';
      }

      quickview() {
        this.loadProductForNotify()
          .then(() => {
            FoxTheme.a11y.trapFocus(this, this.focusElement);
          })
          .catch((e) => {
            console.error(e);
          });
      }

      /**
       * Fetch product section HTML for quick view.
       *
       * @param {string} [variantId]
       * @returns {Promise<void>}
       */
      loadProductForNotify(variantId) {
        const drawerContent = this.querySelector(this.selector);
        const baseUrl = this.dataset.productUrl?.split('?')[0];

        if (!baseUrl) {
          return Promise.reject(new Error('Product URL missing on quick-view-modal'));
        }

        const sectionIds = [
          this.getProductQuickViewSectionId(),
          'quick-view',
          'main-product',
        ].filter(Boolean);

        const uniqueSectionIds = [...new Set(sectionIds)];

        const tryFetch = (index) => {
          if (index >= uniqueSectionIds.length) {
            return Promise.reject(new Error('Could not load product section for notify'));
          }

          const sectionId = uniqueSectionIds[index];
          const sectionUrl = new URL(baseUrl, window.location.origin);
          sectionUrl.searchParams.set('section_id', sectionId);
          if (variantId) sectionUrl.searchParams.set('variant', variantId);

          return fetch(sectionUrl.toString())
            .then((response) => {
              if (!response.ok) throw new Error(`Section fetch failed (${response.status})`);
              return response.text();
            })
            .then((responseText) => {
              const innerHtml = this.extractQuickViewInnerHtml(responseText);
              if (!innerHtml || !responseText.includes('product-info')) {
                throw new Error(`No product-info in section "${sectionId}"`);
              }

              this.setInnerHTML(drawerContent, innerHtml);

              if (window.Shopify && Shopify.PaymentButton) {
                Shopify.PaymentButton.init();
              }

              document.dispatchEvent(
                new CustomEvent('quick-view:loaded', {
                  detail: { productUrl: this.dataset.productUrl, sectionId },
                })
              );
            })
            .catch((error) => {
              if (index < uniqueSectionIds.length - 1) {
                return tryFetch(index + 1);
              }
              return Promise.reject(error);
            });
        };

        return tryFetch(0);
      }

      setInnerHTML(element, innerHTML) {
        element.innerHTML = innerHTML;
        element.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });

        this.classList.remove(this.classesToRemoveOnLoad);
      }
    }
  );
}
